/**
 * Parse and build URL query params for deep-linking into a case.
 *
 * Supported params (first match wins when resolving):
 *   isic_id / isic  — any ISIC image id in the case (typical ISIC Archive link)
 *   rcm_case_id     — RCM case id from the matched CSV
 *   patient_id      — IP_* patient id (optional lesion_id)
 *   lesion_id       — IL_* lesion id (use with patient_id)
 *   collection      — isic | full
 */

export function parseDeepLinkParams(search = '') {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const rawIsic = params.get('isic_id') || params.get('isic');
  return {
    isicId: rawIsic ? normalizeIsicId(rawIsic) : null,
    rcmCaseId: trimParam(params.get('rcm_case_id')),
    patientId: trimParam(params.get('patient_id')),
    lesionId: trimParam(params.get('lesion_id')),
    collection: trimParam(params.get('collection')),
  };
}

export function normalizeIsicId(value) {
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const upper = trimmed.toUpperCase();
  return upper.startsWith('ISIC_') ? upper : `ISIC_${upper.replace(/^ISIC_?/, '')}`;
}

function trimParam(value) {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed || null;
}

export function buildDeepLinkSearch({ patientId, lesionId, collection, isicId, rcmCaseId }) {
  const params = new URLSearchParams();

  if (isicId) {
    params.set('isic_id', normalizeIsicId(isicId));
  } else if (rcmCaseId) {
    params.set('rcm_case_id', rcmCaseId);
  } else {
    if (patientId) params.set('patient_id', patientId);
    if (lesionId) params.set('lesion_id', lesionId);
  }

  if (collection) {
    params.set('collection', collection);
  }

  return params.toString();
}

export function applyDeepLinkSearch(values) {
  const search = buildDeepLinkSearch(values);
  const url = search
    ? `${window.location.pathname}?${search}`
    : window.location.pathname;
  window.history.replaceState(null, '', url);
}

export function hasDeepLinkParams(params) {
  return Boolean(
    params.isicId ||
      params.rcmCaseId ||
      params.patientId ||
      params.lesionId ||
      params.collection
  );
}

/** User-facing message when deep-link IDs cannot be resolved. */
export function buildDeepLinkWarningMessages({
  params,
  target,
  indexAvailable,
  patientFound,
  lesionFound,
}) {
  const messages = [];

  if (params.collection && !resolveCollectionParam(params.collection)) {
    messages.push(`Unknown collection "${params.collection}"; using the default collection.`);
  }

  if (!indexAvailable && hasDeepLinkParams(params)) {
    messages.push('Case index unavailable; could not verify the URL against known cases.');
    return messages;
  }

  if (!target) {
    if (params.isicId) {
      messages.push(`ISIC image ${params.isicId} is not referenced in the case index.`);
    } else if (params.rcmCaseId) {
      messages.push(`RCM case ${params.rcmCaseId} is not referenced in the case index.`);
    } else if (params.patientId && params.lesionId) {
      messages.push(
        `Patient ${params.patientId} / lesion ${params.lesionId} is not referenced in the case index.`
      );
    } else if (params.patientId) {
      messages.push(`Patient ${params.patientId} is not referenced in the case index.`);
    } else {
      messages.push('The requested case could not be found from the URL.');
    }
    return messages;
  }

  if (target.indexMatched === false) {
    if (params.patientId && params.lesionId) {
      messages.push(
        `Patient ${params.patientId} / lesion ${params.lesionId} is not referenced in the case index.`
      );
    } else if (params.patientId) {
      messages.push(`Patient ${params.patientId} is not referenced in the case index.`);
    }
  }

  if (target.patientId && patientFound === false) {
    messages.push(
      `Patient ${target.patientId} was not found in the selected collection on the server.`
    );
  }

  if (target.lesionId && lesionFound === false && patientFound !== false) {
    messages.push(
      `Lesion ${target.lesionId} was not found for patient ${target.patientId}; showing another lesion instead.`
    );
  }

  return messages;
}

function resolveCollectionParam(value) {
  const normalized = value?.toLowerCase();
  if (normalized === 'isic' || normalized === 'full') {
    return normalized;
  }
  return null;
}
